import { useEffect } from "react";
import { useSiteSettings } from "../../lib/settings";
import { readConsent } from "./CookieBanner";

/* ------------------------------------------------------------------
 * TrackingScripts — dynamically injects analytics/marketing/support
 * scripts ONLY after the visitor has consented to the corresponding
 * cookie category. Loads Google Tag Manager, GA4, Google Ads, Meta
 * Pixel, TikTok Pixel, LinkedIn Insight, Hotjar, JivoChat, Tawk.to.
 * Reacts live to consent changes via the "firat:consent-change" event.
 * ------------------------------------------------------------------ */

const loaded = new Set();

const inject = (id, tag) => {
  if (loaded.has(id)) return;
  loaded.add(id);
  document.head.appendChild(tag);
};

const injectScript = (id, src, extra = {}) => {
  if (loaded.has(id) || !src) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  Object.assign(s, extra);
  s.setAttribute("data-firat-tracker", id);
  document.head.appendChild(s);
  loaded.add(id);
};

const injectInline = (id, code) => {
  if (loaded.has(id) || !code) return;
  const s = document.createElement("script");
  s.textContent = code;
  s.setAttribute("data-firat-tracker", id);
  document.head.appendChild(s);
  loaded.add(id);
};

const applyConsent = (settings) => {
  const c = readConsent();
  if (!c) return;

  // ---- ANALYTICS ----
  if (c.analytics) {
    if (settings.google_tag_manager_id) {
      injectInline(`gtm-init-${settings.google_tag_manager_id}`,
        `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${settings.google_tag_manager_id}');`);
    }
    if (settings.ga4_measurement_id) {
      injectScript(`ga4-${settings.ga4_measurement_id}`, `https://www.googletagmanager.com/gtag/js?id=${settings.ga4_measurement_id}`);
      injectInline(`ga4-init-${settings.ga4_measurement_id}`,
        `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${settings.ga4_measurement_id}', {anonymize_ip:true});`);
    }
    if (settings.hotjar_id) {
      injectInline(`hotjar-${settings.hotjar_id}`,
        `(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:${settings.hotjar_id},hjsv:6};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`);
    }
  }

  // ---- MARKETING ----
  if (c.marketing) {
    if (settings.google_ads_id) {
      // Google Ads shares gtag with GA4; only add config if GA4 not already loaded
      if (!settings.ga4_measurement_id) {
        injectScript(`ads-${settings.google_ads_id}`, `https://www.googletagmanager.com/gtag/js?id=${settings.google_ads_id}`);
        injectInline(`ads-init-${settings.google_ads_id}`,
          `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${settings.google_ads_id}');`);
      } else {
        injectInline(`ads-config-${settings.google_ads_id}`,
          `if (window.gtag) gtag('config', '${settings.google_ads_id}');`);
      }
    }
    if (settings.meta_pixel_id) {
      injectInline(`meta-pixel-${settings.meta_pixel_id}`,
        `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${settings.meta_pixel_id}');fbq('track','PageView');`);
    }
    if (settings.tiktok_pixel_id) {
      injectInline(`tiktok-${settings.tiktok_pixel_id}`,
        `!function (w, d, t) {w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var i=document.createElement("script");i.type="text/javascript",i.async=!0,i.src=r+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(i,a)};ttq.load('${settings.tiktok_pixel_id}');ttq.page();}(window, document, 'ttq');`);
    }
    if (settings.linkedin_insight_id) {
      injectInline(`linkedin-${settings.linkedin_insight_id}`,
        `_linkedin_partner_id="${settings.linkedin_insight_id}";window._linkedin_data_partner_ids=window._linkedin_data_partner_ids||[];window._linkedin_data_partner_ids.push(_linkedin_partner_id);(function(l){if(!l){window.lintrk=function(a,b){window.lintrk.q.push([a,b])};window.lintrk.q=[]}var s=document.getElementsByTagName('script')[0];var b=document.createElement('script');b.type='text/javascript';b.async=true;b.src='https://snap.licdn.com/li.lms-analytics/insight.min.js';s.parentNode.insertBefore(b,s);})(window.lintrk);`);
    }
  }

  // ---- SUPPORT ----
  if (c.support) {
    if (settings.jivochat_id) {
      injectScript(`jivo-${settings.jivochat_id}`, `//code.jivosite.com/widget/${settings.jivochat_id}`);
    }
    if (settings.tawk_property_id) {
      const wid = settings.tawk_widget_id || "default";
      injectInline(`tawk-${settings.tawk_property_id}`,
        `var Tawk_API=Tawk_API||{},Tawk_LoadStart=new Date();(function(){var s1=document.createElement("script"),s0=document.getElementsByTagName("script")[0];s1.async=true;s1.src='https://embed.tawk.to/${settings.tawk_property_id}/${wid}';s1.charset='UTF-8';s1.setAttribute('crossorigin','*');s0.parentNode.insertBefore(s1,s0);})();`);
    }
  }
};

const TrackingScripts = () => {
  const settings = useSiteSettings();
  useEffect(() => {
    if (!settings) return;
    applyConsent(settings);
    const handler = () => applyConsent(settings);
    window.addEventListener("firat:consent-change", handler);
    return () => window.removeEventListener("firat:consent-change", handler);
  }, [settings]);
  return null;
};

export default TrackingScripts;
